import React, { useEffect, useRef, useState } from "react";

/*
 * Count-up number that animates from 0 to `value` the first time it scrolls
 * into view (the stat-band animation used on appinventiv.com). Respects
 * prefers-reduced-motion by jumping straight to the final value.
 *
 * Props: value (number), prefix, suffix, duration (ms)
 */
function Counter({ value, prefix = "", suffix = "", duration = 1600 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      setDisplay(value);
      return undefined;
    }

    let frame = 0;
    let started = false;

    const run = () => {
      let startTime = null;
      const step = (now) => {
        if (startTime === null) {
          startTime = now;
        }
        const progress = Math.min((now - startTime) / duration, 1);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(Math.round(eased * value));
        if (progress < 1) {
          frame = requestAnimationFrame(step);
        }
      };
      frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            run();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export default Counter;
